import { buttonClasses, Components, circularProgressClasses, svgIconClasses, Theme } from '@mui/material';
import { GetThemeOptions } from '../../theme/theme';
import { toPixelString } from '../../utils';

// Padding used in buttons (in pixels) for each size option
const buttonPadding = {
  small: [2, 12],
  medium: [6, 16],
};

/**
 * Component overrides (i.e. global styles, default props) applied to the MUI
 * Button to make it match the design system.
 *
 * See: https://mui.com/material-ui/api/button/#css
 */
export const getMuiButton = (opts: GetThemeOptions): Components<Theme>['MuiButton'] => ({
  defaultProps: {
    disableElevation: true,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      ...theme.typography.button,
      ...(opts.panwRebrandStubYear && { borderRadius: theme.shape.borderRadiusSm }),
      // matching the color of the progress indicator to the button text color
      [`.${circularProgressClasses.root}`]: {
        color: 'inherit',
      },
    }),

    sizeSmall: {
      padding: toPixelString(buttonPadding.small),
      [`.${svgIconClasses.root}`]: {
        width: '16px',
        height: '16px',
      },
    },

    sizeMedium: {
      padding: toPixelString(buttonPadding.medium),
      [`.${svgIconClasses.root}`]: {
        width: '20px',
        height: '20px',
      },
    },

    // For outlined buttons, we need to subtract 1px for the border to ensure
    // they're the same size as the other variants
    outlinedSizeSmall: {
      padding: toPixelString(buttonPadding.small.map((p) => p - 1)),
    },
    outlinedSizeMedium: {
      padding: toPixelString(buttonPadding.medium.map((p) => p - 1)),
    },

    // This is what our "primary" variant in chrono-ui maps to
    containedPrimary: ({ theme }) => ({
      backgroundColor: theme.palette.button.standard.primary.default,
      color: theme.palette.button.standard.primary.text,

      [`&:hover`]: {
        backgroundColor: theme.palette.button.standard.primary.hover,
        color: theme.palette.button.standard.primary.text,
      },

      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.button.standard.primary.disabled,
        color: theme.palette.text.disabled,
      },
    }),
    // This is what our "outlined" variant in chrono-ui maps to
    outlinedSecondary: ({ theme }) => ({
      backgroundColor: theme.palette.button.standard.secondary.default,
      color: theme.palette.button.standard.secondary.text,
      borderColor: theme.palette.button.standard.secondary.borderColor.default ?? 'transparent',

      [`&:hover`]: {
        backgroundColor: theme.palette.button.standard.secondary.hover,
        borderColor: theme.palette.button.standard.secondary.borderColor.hover ?? 'transparent',
      },

      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.button.standard.secondary.disabled,
        color: theme.palette.button.standard.secondary.textDisabled,
        borderColor: theme.palette.button.standard.secondary.borderColor.disabled ?? 'transparent',
      },
    }),

    // This is what our "text" variant in chrono-ui maps to
    textPrimary: ({ theme }) => ({
      color: theme.palette.button.standard.tertiary.text,

      [`&:hover`]: {
        color: theme.palette.button.standard.secondary.text,
        backgroundColor: theme.palette.button.standard.tertiary.hover,
      },

      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.button.standard.tertiary.disabled,
        color: theme.palette.text.disabled,
      },
    }),

    // This is what our "danger primary" variant in chrono-ui maps to
    containedError: ({ theme }) => ({
      backgroundColor: theme.palette.button.danger.primary.default,
      color: theme.palette.button.danger.primary.text,

      [`&:hover`]: {
        backgroundColor: theme.palette.button.danger.primary.hover,
      },
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.button.danger.primary.disabled,
        color: theme.palette.text.disabled,
      },
    }),

    // This is what our "danger text" variant in chrono-ui maps to
    textError: ({ theme }) => ({
      color: theme.palette.button.danger.tertiary.text,

      [`&:hover`]: {
        backgroundColor: theme.palette.button.danger.tertiary.hover,
      },

      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.button.danger.tertiary.disabled,
        color: theme.palette.button.danger.tertiary.textDisabled,
      },
    }),
  },
});
